import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Loader2, CheckCircle2, FileText, X } from "lucide-react";
import { buildingsApi, clientPortalApi } from "@/services/api";
import { apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function PortalUploadReport() {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState("");
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { buildingsApi.getAll().then((r) => setBuildings(r.data || [])).catch(() => {}); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please upload a PDF file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const b64 = res.includes(",") ? res.split(",")[1] : res;
      setFile({ name: f.name, pdf_base64: b64, size: f.size });
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!file) { toast.error("Please select the PDF to upload."); return; }
    setSaving(true);
    try {
      await clientPortalApi.uploadExternalReport({
        building_id: buildingId || undefined,
        filename: file.name,
        pdf_base64: file.pdf_base64,
        notes: notes.trim() || undefined,
      });
      setDone(true);
      toast.success("Report uploaded");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg py-10 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
        <h2 className="text-xl font-bold text-slate-800">Report received</h2>
        <p className="mt-1 text-sm text-slate-500">FD Check will extract the failing doorsets and prepare a quote for you.</p>
        <Button className="mt-5" onClick={() => navigate("/portal")} data-testid="upload-home">Back to home</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Upload an inspection report" subtitle="Send us a 3rd-party PDF and we'll quote the remedials" testId="upload-report-header" />
      <Card className="space-y-4 p-5">
        <div className="space-y-1.5">
          <Label>Building (optional)</Label>
          <Select value={buildingId} onValueChange={setBuildingId}>
            <SelectTrigger data-testid="upload-building"><SelectValue placeholder="Select a building" /></SelectTrigger>
            <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Inspection PDF</Label>
          {file ? (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="flex min-w-0 items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-primary" /><span className="truncate">{file.name}</span></span>
              <button onClick={() => setFile(null)} className="text-slate-400 hover:text-red-500" data-testid="upload-remove"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 hover:bg-slate-50">
              <Upload className="h-4 w-4" /> Choose PDF
              <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="upload-file-input" />
            </label>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Notes (optional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know…" data-testid="upload-notes" />
        </div>
        <Button onClick={submit} disabled={saving} className="w-full" data-testid="upload-submit">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload &amp; request quote
        </Button>
      </Card>
    </div>
  );
}
